import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getStickerContext,
  buildPackageRows,
  duplicateForStickers,
  countPackages,
  formatConsigneeBlock,
} from '../../utils/collectionPackages';
import {
  generateQrDataUrl,
  generateBarcodeSvg,
  downloadStickersPdf,
  stickerPdfFilename,
  A6_W_MM,
  A6_H_MM,
} from '../../utils/packageStickerExport';
import { buildPackageTrackUrl } from '../../utils/packageTrack';

/** Scale HBL digits to use maximum available width on the sticker. */
function AutoSizeHbl({ text, variant }) {
  const wrapRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const el = textRef.current;
    if (!wrap || !el) return;

    const maxPt = 104;
    const minPt = variant === 'doorToDoor' ? 38 : 40;
    let size = maxPt;
    el.style.fontSize = `${size}pt`;

    const fit = () => {
      const maxW = wrap.clientWidth;
      if (!maxW) return;
      size = maxPt;
      el.style.fontSize = `${size}pt`;
      while (size > minPt && el.scrollWidth > maxW) {
        size -= 1;
        el.style.fontSize = `${size}pt`;
      }
    };

    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [text, variant]);

  return (
    <div ref={wrapRef} className="pkg-sticker-hbl-wrap">
      <div ref={textRef} className="pkg-sticker-hbl">
        {text}
      </div>
    </div>
  );
}

function AutoSizeConsignee({ name, address, phone }) {
  const blockRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const block = blockRef.current;
    const inner = innerRef.current;
    if (!block || !inner) return;

    const nameEl = inner.querySelector('.pkg-sticker-consignee-name');
    const lineEls = inner.querySelectorAll('.pkg-sticker-consignee-line');
    let namePt = 20;
    let linePt = 17;
    const minName = 11;
    const minLine = 10;

    const apply = () => {
      if (nameEl) nameEl.style.fontSize = `${namePt}pt`;
      lineEls.forEach((el) => { el.style.fontSize = `${linePt}pt`; });
    };

    const fit = () => {
      namePt = 20;
      linePt = 17;
      apply();
      const maxH = block.clientHeight;
      if (!maxH) return;
      while ((namePt > minName || linePt > minLine) && inner.scrollHeight > maxH + 1) {
        if (linePt > minLine) linePt -= 0.5;
        if (namePt > minName) namePt -= 0.5;
        apply();
      }
    };

    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(block);
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      if (ro) ro.disconnect();
    };
  }, [name, address, phone]);

  return (
    <div ref={blockRef} className="pkg-sticker-consignee">
      <div ref={innerRef} className="pkg-sticker-consignee-inner">
        <div className="pkg-sticker-consignee-name">{name}</div>
        <div className="pkg-sticker-consignee-line pkg-sticker-consignee-addr">{address}</div>
        <div className="pkg-sticker-consignee-line pkg-sticker-consignee-phone">{phone}</div>
      </div>
    </div>
  );
}

function StickerCodes({ packageCode, variant = 'standard' }) {
  const [qrUrl, setQrUrl] = useState('');
  const [barcodeSvg, setBarcodeSvg] = useState('');
  const isD2d = variant === 'doorToDoor';
  const qrSize = isD2d ? 88 : 140;

  useEffect(() => {
    let cancelled = false;
    const trackUrl = buildPackageTrackUrl(packageCode);
    generateQrDataUrl(trackUrl, qrSize).then((url) => {
      if (!cancelled) setQrUrl(url);
    });
    setBarcodeSvg(generateBarcodeSvg(packageCode, { height: isD2d ? 40 : 56 }));
    return () => { cancelled = true; };
  }, [packageCode, qrSize, isD2d]);

  return (
    <div className={`pkg-sticker-codes pkg-sticker-codes--${variant}`}>
      <div className="pkg-sticker-qr">
        {qrUrl ? (
          <img src={qrUrl} alt="QR" />
        ) : (
          <div className="pkg-sticker-qr-placeholder" />
        )}
      </div>
      <div
        className="pkg-sticker-barcode"
        dangerouslySetInnerHTML={{ __html: barcodeSvg }}
      />
    </div>
  );
}

function StickerSheet({ row }) {
  const isD2d = row.template === 'doorToDoor';

  return (
    <div className={`pkg-sticker-sheet pkg-sticker-sheet--${isD2d ? 'd2d' : 'standard'}`}>
      <div className={`pkg-sticker-inner${isD2d ? ' pkg-sticker-inner--d2d' : ''}`}>
        <div className="pkg-sticker-top">
          <div className="pkg-sticker-top-left">
            <AutoSizeHbl text={row.hblDisplay} variant={isD2d ? 'doorToDoor' : 'standard'} />
          </div>
          <div className="pkg-sticker-sub">({row.subNo})</div>
        </div>

        {isD2d && (
          <AutoSizeConsignee
            name={row.consigneeName}
            address={row.consigneeAddress}
            phone={row.consigneePhone}
          />
        )}

        {isD2d ? (
          <StickerCodes packageCode={row.packageCode} variant="doorToDoor" />
        ) : (
          <>
            <StickerCodes packageCode={row.packageCode} variant="standard" />
            <div className="pkg-sticker-brand">Jet shipping cargo</div>
          </>
        )}
      </div>
    </div>
  );
}

const PackageStickers = ({
  collection,
  parentCollection,
  consignee,
  childCollections = [],
  onClose,
  onEnsurePackages,
}) => {
  const printRef = useRef(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [ensuring, setEnsuring] = useState(false);

  const context = useMemo(
    () => getStickerContext(collection, { parentCollection, consignee, childCollections }),
    [collection, parentCollection, consignee, childCollections]
  );

  const packageRows = useMemo(() => buildPackageRows(collection, context), [collection, context]);
  const stickerRows = useMemo(() => duplicateForStickers(packageRows, 2), [packageRows]);
  const packages = countPackages(packageRows);

  useEffect(() => {
    if (!context.canPrint || !onEnsurePackages) return;
    let cancelled = false;
    setEnsuring(true);
    const body = {
      parentHblNo: context.parentHblNo || '',
      consigneeSnapshot:
        context.template === 'doorToDoor' && context.consigneeBlock
          ? context.consigneeBlock
          : undefined,
    };
    Promise.resolve(onEnsurePackages(body))
      .catch((e) => console.warn('Package ensure:', e))
      .finally(() => {
        if (!cancelled) setEnsuring(false);
      });
    return () => { cancelled = true; };
  }, [collection, context, onEnsurePackages]);

  const handlePrint = () => window.print();

  const handleDownloadPdf = useCallback(async () => {
    const root = printRef.current;
    if (!root) return;
    const sheets = root.querySelectorAll('.pkg-sticker-sheet');
    if (!sheets.length) return;
    setDownloadBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 250));
      await downloadStickersPdf(Array.from(sheets), stickerPdfFilename(collection?.collectionNo));
    } catch (e) {
      alert('PDF download failed: ' + (e.message || 'Unknown error'));
    } finally {
      setDownloadBusy(false);
    }
  }, [collection]);

  if (!context.canPrint) {
    return (
      <div className="pkg-stickers-root">
        <div className="pkg-stickers-toolbar pkg-hide-print">
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <div className="pkg-stickers-blocked">
          <p>{context.blockReason || 'Cannot print stickers for this collection.'}</p>
        </div>
      </div>
    );
  }

  if (packages === 0) {
    return (
      <div className="pkg-stickers-root">
        <div className="pkg-stickers-toolbar pkg-hide-print">
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <div className="pkg-stickers-blocked">
          <p>No packages in this collection. Add boxes with quantity first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pkg-stickers-root">
      <style>{`
        .pkg-stickers-root {
          background: #e5e7eb;
          min-height: 100vh;
          padding: 12px 0 24px;
        }
        .pkg-stickers-toolbar {
          max-width: ${A6_W_MM}mm;
          margin: 0 auto 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 0 8px;
        }
        .pkg-stickers-toolbar button {
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          background: #fff;
          color: #1e293b;
          cursor: pointer;
          font-weight: 600;
        }
        .pkg-stickers-toolbar button.primary {
          background: #0056f1;
          color: #fff;
          border-color: #0056f1;
        }
        [data-theme="dark"] .pkg-stickers-root {
          background: #0b1220;
        }
        [data-theme="dark"] .pkg-stickers-summary {
          color: #cbd5e1;
        }
        [data-theme="dark"] .pkg-stickers-toolbar button:not(.primary) {
          background: #334155;
          color: #f8fafc;
          border-color: #64748b;
        }
        [data-theme="dark"] .pkg-stickers-toolbar button:not(.primary):hover {
          background: #475569;
        }
        [data-theme="dark"] .pkg-stickers-blocked {
          color: #e2e8f0;
        }
        .pkg-stickers-summary {
          font-size: 0.875rem;
          color: #475569;
        }
        .pkg-sticker-sheet {
          width: ${A6_W_MM}mm;
          height: ${A6_H_MM}mm;
          margin: 0 auto 12px;
          background: #fff;
          box-sizing: border-box;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
          page-break-after: always;
          overflow: hidden;
        }
        .pkg-sticker-inner {
          padding: 5mm 7mm 4mm;
          height: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
        }
        .pkg-sticker-inner--d2d {
          padding: 4mm 6mm 3mm;
        }
        .pkg-sticker-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 3mm;
          margin-bottom: 0;
          flex-shrink: 0;
        }
        .pkg-sticker-top-left {
          flex: 1;
          min-width: 0;
          max-width: calc(100% - 16mm);
        }
        .pkg-sticker-hbl-wrap {
          width: 100%;
          overflow: hidden;
        }
        .pkg-sticker-hbl {
          font-size: 76pt;
          font-weight: 900;
          letter-spacing: 0.02em;
          line-height: 0.88;
          white-space: nowrap;
          display: inline-block;
          max-width: 100%;
        }
        .pkg-sticker-sub {
          flex-shrink: 0;
          font-size: 24pt;
          font-weight: 800;
          line-height: 1;
          padding-top: 1.5mm;
          min-width: 14mm;
          text-align: right;
        }
        .pkg-sticker-sheet--d2d .pkg-sticker-sub {
          font-size: 24pt;
        }
        /* Door to Door */
        .pkg-sticker-sheet--d2d .pkg-sticker-top-left {
          max-width: calc(100% - 18mm);
        }
        .pkg-sticker-sheet--d2d .pkg-sticker-consignee {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 1.5mm 0 2mm;
          overflow: hidden;
        }
        .pkg-sticker-consignee-inner {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 2mm;
          line-height: 1.22;
        }
        .pkg-sticker-sheet--d2d .pkg-sticker-consignee-name {
          font-weight: 800;
          font-size: 20pt;
          word-break: break-word;
        }
        .pkg-sticker-sheet--d2d .pkg-sticker-consignee-line {
          font-weight: 700;
          font-size: 17pt;
          word-break: break-word;
        }
        .pkg-sticker-codes--doorToDoor {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 5mm;
          flex-shrink: 0;
          padding-top: 1mm;
        }
        .pkg-sticker-codes--doorToDoor .pkg-sticker-qr img,
        .pkg-sticker-codes--doorToDoor .pkg-sticker-qr-placeholder {
          width: 18mm;
          height: 18mm;
          display: block;
        }
        .pkg-sticker-codes--doorToDoor .pkg-sticker-barcode {
          flex: 1;
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;
          min-height: 20mm;
        }
        .pkg-sticker-codes--doorToDoor .pkg-sticker-barcode svg {
          width: 100%;
          max-width: 95mm;
          height: auto;
          max-height: 18mm;
        }
        /* UPB / Gift standard */
        .pkg-sticker-sheet--standard .pkg-sticker-codes--standard {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8mm;
          padding: 4mm 0 2mm;
        }
        .pkg-sticker-codes--standard .pkg-sticker-qr img,
        .pkg-sticker-codes--standard .pkg-sticker-qr-placeholder {
          width: 34mm;
          height: 34mm;
          display: block;
        }
        .pkg-sticker-codes--standard .pkg-sticker-barcode {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 38mm;
        }
        .pkg-sticker-codes--standard .pkg-sticker-barcode svg {
          width: 100%;
          max-width: 88mm;
          height: auto;
          max-height: 28mm;
        }
        .pkg-sticker-brand {
          text-align: center;
          font-size: 11pt;
          font-weight: 600;
          letter-spacing: 0.02em;
          margin-top: auto;
          padding-top: 1mm;
        }
        .pkg-stickers-blocked {
          max-width: 480px;
          margin: 48px auto;
          padding: 24px;
          background: #fff;
          border-radius: 12px;
          text-align: center;
          color: #64748b;
        }
        @media print {
          .pkg-hide-print { display: none !important; }
          .pkg-stickers-root { background: #fff; padding: 0; }
          .pkg-sticker-sheet {
            margin: 0;
            box-shadow: none;
            page-break-after: always;
          }
          @page {
            size: ${A6_W_MM}mm ${A6_H_MM}mm landscape;
            margin: 0;
          }
        }
      `}</style>

      <div className="pkg-stickers-toolbar pkg-hide-print">
        <div className="pkg-stickers-summary">
          {packages} package{packages !== 1 ? 's' : ''} → {stickerRows.length} stickers (A6 landscape)
          {ensuring ? ' · Registering…' : ''}
          {context.template === 'doorToDoor' ? ' · Door to Door' : ' · UPB/Gift'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onClose}>Close</button>
          <button type="button" onClick={handlePrint}>Print (A6 landscape)</button>
          <button type="button" className="primary" onClick={handleDownloadPdf} disabled={downloadBusy}>
            {downloadBusy ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      </div>

      <div ref={printRef}>
        {stickerRows.map((row, idx) => (
          <StickerSheet key={`${row.key}-${row.copyIndex}-${idx}`} row={row} />
        ))}
      </div>
    </div>
  );
};

export { formatConsigneeBlock };
export default PackageStickers;
